/* eslint-disable no-use-before-define */
import { Warning } from '@mui/icons-material';
import { Box, Checkbox, ListItemText, MenuItem, SelectChangeEvent, Tooltip } from '@mui/material';
import type { MRT_ColumnDef as ColumnDef } from 'material-react-table';
import * as React from 'react';
import { useSearchParams } from 'react-router';
import DialogButtons from '~/components/Commons/ActionConfirmDialog/DialogButtons';
import dialog from '~/components/Commons/ActionConfirmDialog/useDialog';
import DataTable from '~/components/Commons/DataTable/DataTable';
import StyledCheckbox from '~/components/Form/Checkbox';
import TextField from '~/components/Form/TextField/TextField';
import { createForm } from '~/context/form/form';
import { useClientSessionQuery } from '~/hooks/queries/useSessionPlQuery';
import useDebouncedValue from '~/hooks/useDebouncedValue';
import { useOverallCaps } from '../../AdManager/OverallCaps/utils';
import { useCapsMutation } from './useCapsMutation';
import { ForecastData, ValidGeoSchema, geoArray, useForecastQuery } from './useForecastQuery';

type PublisherId = number;
type FormValue = Record<PublisherId, string>;

const { useFormValue, Provider, useAllFormData } = createForm(
  {},
  (prev: FormValue, next: FormValue) => ({
    ...prev,
    ...next,
  }),
);

export { Provider as ForecastProvider };

function getPercentageOf(total: number, percent: number) {
  return (total * percent) / 100;
}

const roundAndFormat = (num: number) => Number(num.toFixed(0));
export function Forecaster() {
  const clientSessionQuery = useClientSessionQuery();
  const currency = clientSessionQuery.data?.currency_pdf_name ?? '';
  const [searchParams, setSearchParams] = useSearchParams({
    geo: 'us',
  });
  const geos = searchParams.getAll('geo') as ValidGeoSchema;

  const excludedPartnerIds = React.useMemo(
    () => searchParams.getAll('ex_partner_id'),
    [searchParams],
  );

  const searchParamBudget = searchParams.get('budget');
  const budget = Number(searchParamBudget ?? '0') < 100 ? 0 : Number(searchParamBudget ?? '0');
  const forecastQuery = useForecastQuery(geos, excludedPartnerIds);
  const overallCapsQuery = useOverallCaps(
    clientSessionQuery?.data?.id,
    clientSessionQuery?.data?.currency_pdf_name,
  );

  const columns: ColumnDef<ForecastData[number]>[] = [
    {
      header: 'Publisher Id',
      accessorKey: 'partnerId',
      Cell: ({ renderedCellValue }) =>
        renderedCellValue === -1 || renderedCellValue === 0 ? null : renderedCellValue,
      size: 0,
    },
    {
      header: 'Publisher Name',
      accessorKey: 'partnerName',
      Cell: ({ row }) => {
        const { partnerId, partnerName } = row.original;
        const currentPubCap = overallCapsQuery.data?.find((cap) => cap.partnerId === partnerId);

        return (
          <Box
            sx={{
              display: 'flex',
            }}
          >
            {partnerName}
            {currentPubCap ? (
              <Tooltip
                title="A booking already exists for this partner. We recommend you edit that one or exclude
                  this partner from the forecast"
                slotProps={{
                  popper: {
                    sx: { zIndex: 3000 },
                  },
                }}
              >
                <Warning color="warning" />
              </Tooltip>
            ) : null}
          </Box>
        );
      },
    },
    {
      header: 'Percent',
      accessorKey: 'percent',
      size: 100,
      muiTableBodyCellProps: {
        align: 'right',
      },
      Cell: ({ renderedCellValue }) => `${budget ? renderedCellValue : 0}%`,
      Footer: ({ table }) => {
        const totalPercentage = table
          .getPaginationRowModel()
          .rows.reduce((sum, row) => sum + Math.round(Number(row.original.percent)), 0);
        return (
          <Box sx={{ width: '100%', textAlign: 'end', paddingTop: '2px' }}>
            Total: {budget ? roundAndFormat(totalPercentage) : 0}%
          </Box>
        );
      },
    },
    {
      header: 'Recommended Budget',
      accessorFn: ({ partnerId }) => `${partnerId}-recommended-budget`,
      id: 'recommended-budget',
      enableSorting: false,
      enableColumnFilter: false,
      muiTableBodyCellProps: {
        align: 'right',
      },
      Cell: ({ row }) => {
        const { percent } = row.original;
        return roundAndFormat(getPercentageOf(budget, percent));
      },
      Footer: ({ table }) => {
        const totalRecommendedBudget = table
          .getPaginationRowModel()
          .rows.reduce(
            (sum, row) => sum + getPercentageOf(budget, Math.round(row.original.percent)),
            0,
          );
        return (
          <Box sx={{ width: '100%', textAlign: 'end', paddingTop: '2px' }}>
            Total: {currency}
            {roundAndFormat(totalRecommendedBudget)}
          </Box>
        );
      },
    },
    {
      header: 'Include in Forecast',
      accessorFn: ({ partnerId }) => `${partnerId}-remove-publisher`,
      enableSorting: false,
      enableColumnFilter: false,
      id: 'remove-publisher',
      Cell: ({ row }) => {
        const { partnerId } = row.original;
        const selected = !excludedPartnerIds.includes(String(partnerId));
        return (
          <StyledCheckbox
            checked={selected}
            onChange={(e, checked) => {
              setSearchParams((prev) => {
                if (!checked) {
                  prev.append('ex_partner_id', String(partnerId));
                } else {
                  prev.delete('ex_partner_id', String(partnerId));
                }
                return prev;
              });
            }}
            label=""
            sx={{ margin: 0 }}
          />
        );
      },
      Footer: ({ table }) => {
        const allRows = table.getRowModel().rows;
        const allSelected = allRows.length !== new Set(excludedPartnerIds).size;
        return (
          <>
            <StyledCheckbox
              checked={allSelected}
              onChange={() => {
                setSearchParams((prev) => {
                  if (allSelected) {
                    allRows.forEach((row) => {
                      prev.append('ex_partner_id', String(row.original.partnerId));
                    });
                  } else {
                    prev.delete('ex_partner_id');
                  }
                  return prev;
                });
              }}
              label={allSelected ? 'Deselect All' : 'Select All'}
              sx={{
                color: 'primary.main',
                '& .MuiFormControlLabel-label': {
                  fontSize: 'unset',
                  fontWeight: 'bold',
                },
                margin: '0',
              }}
            />
          </>
        );
      },
    },
    {
      header: 'Customize Budget',
      id: 'custom-budget',
      enableSorting: false,
      enableColumnFilter: false,
      accessorFn: ({ partnerId }) => `${partnerId}-custom-budget`,
      Cell: ({ row }) => {
        const recommendedBudget = roundAndFormat(getPercentageOf(budget, row.original.percent));
        return (
          <CustomizeRecommendationInput
            currency={currency}
            data={row.original}
            recommendedBudget={recommendedBudget}
          />
        );
      },
      Footer: ({ table }) => {
        const [allFormData] = useAllFormData();
        const tableData = table.getPaginationRowModel().rows;
        const totalEditedBudget = roundAndFormat(
          tableData.reduce((acc, row) => {
            const { partnerId } = row.original;
            const editedPartnerIds = Object.keys(allFormData);
            if (editedPartnerIds.includes(String(partnerId))) {
              return acc + Number(allFormData[partnerId]);
            }
            return acc + getPercentageOf(budget, Math.round(row.original.percent));
          }, 0),
        );

        function getColor(total: number, b: number) {
          if (total > b || total < b) {
            return 'error.main';
          }

          return 'success.main';
        }

        return (
          <Box
            sx={{
              width: '100%',
              textAlign: 'end',
              color: getColor(totalEditedBudget, budget),
              paddingTop: '2px',
            }}
          >
            Total: {currency}
            {totalEditedBudget}
          </Box>
        );
      },
    },
  ];

  return (
    <div>
      <Box
        component="details"
        sx={{
          padding: '10px',
          maxWidth: '900px',
          '& summary': {
            cursor: 'pointer',
            outline: 'none',
            '&:focus': {
              outline: 'none',
            },
          },
        }}
      >
        <summary>How to use this forecaster</summary>
        To create a budget forecast for bookings on the Dianomi Marketplace, which may also cover
        specific Apple News channels from selected publishers, please follow these steps:
        <Box
          component="ol"
          sx={{
            '& li': {
              listStyle: 'unset',
            },
          }}
        >
          <li>Select the country or countries for which you want to generate a forecast.</li>
          <li>Enter your budget amount.</li>
        </Box>
        Once you do this, you&apos;ll instantly see suggested booking amounts per line item, derived
        from recent traffic data. The below forecasted numbers are based on recent historical data.
        <p>
          If you wish to access a greater amount of supply in any of the below publishers please
          contact your account manager at{' '}
          <a
            href={`mailto:${clientSessionQuery.data?.client.account_manager.email}`}
            target="_blank"
            rel="noreferrer"
          >
            {clientSessionQuery.data?.client.account_manager.email}
          </a>
        </p>
      </Box>
      <DataTable
        data={forecastQuery.data ?? []}
        columns={columns}
        state={{ isFetching: forecastQuery.isFetching, isLoading: forecastQuery.isPending }}
        enablePagination={false}
        enableStickyFooter
        muiTableContainerProps={{
          sx: {
            // sorry for the !important - that's ok
            maxHeight: '55vh !important',
          },
        }}
        Actions={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <GeoSelector />
            <BudgetInput />
          </Box>
        }
      />
      <ForecasterDialogButtons baseData={forecastQuery.data ?? []} />
    </div>
  );
}

function ForecasterDialogButtons({ baseData }: { baseData: ForecastData }) {
  const [allFormData] = useAllFormData();
  const [searchParams] = useSearchParams();
  const capsMutation = useCapsMutation();
  const budget = Number(searchParams.get('budget') ?? '0');
  const handleSaveClick = async () => {
    const postData = baseData.map((row) => {
      return {
        partnerId: row.partnerId,
        amount: Number(allFormData[row.partnerId] ?? getPercentageOf(budget, row.percent)),
        period: 'O',
        clientEditable: 1,
      };
    });
    capsMutation.mutate(postData);
  };
  if (budget === 0) {
    return <DialogButtons handleDialogClose={() => dialog.close()} />;
  }
  return (
    <DialogButtons
      acceptText="Create Bookings"
      handleDialogClose={() => dialog.close()}
      handleDialogConfirm={handleSaveClick}
      isSubmitting={capsMutation.isPending}
    />
  );
}

function CustomizeRecommendationInput({
  data,
  recommendedBudget,
  currency,
}: {
  data: ForecastData[number];
  recommendedBudget: string | number;
  currency: string;
}) {
  const [customBudget, setCustomBudget] = useFormValue(data.partnerId);
  return (
    <TextField
      value={customBudget ?? recommendedBudget}
      onChange={(e) => setCustomBudget({ [data.partnerId]: e.target.value })}
      name={`${data.partnerId}-custom-budget`}
      id={`${data.partnerId}-custom-budget`}
      prefix={currency}
    />
  );
}

function appendAllGeos(searchParams: URLSearchParams) {
  geoArray.forEach((geo) => searchParams.append('geo', geo));
}

function GeoSelector() {
  const [searchParams, setSearchParams] = useSearchParams({
    geo: 'us',
  });
  const geos = searchParams.getAll('geo');

  const handleGeoChange = (e: SelectChangeEvent<unknown>) => {
    setSearchParams((prev) => {
      prev.delete('geo');
      prev.delete('ex_partner_id');
      const { value } = e.target;
      if (typeof value === 'string') {
        if (value === 'all') {
          appendAllGeos(prev);
        } else {
          prev.append('geo', value);
        }
      } else {
        const castValue = value as string[];
        if (castValue.includes('all')) {
          appendAllGeos(prev);
        } else {
          castValue.forEach((geo) => prev.append('geo', geo));
        }
      }
      return prev;
    });
  };

  return (
    <TextField
      select
      SelectProps={{
        onChange: handleGeoChange,
        value: geos,
        multiple: true,
        renderValue: (selected) => {
          if ((selected as string[]).includes('all')) {
            return 'All';
          }
          return (selected as string[]).sort().join(', ').toLocaleUpperCase();
        },
      }}
      sx={{ minWidth: '160px' }}
      size="small"
      label="Forecast Geos"
      name="forecast-geos"
      id="forecast-geos"
    >
      {geoArray.sort().map((geo) => {
        return (
          <MenuItem value={geo}>
            <Checkbox checked={geos.includes(geo)} />
            <ListItemText primary={geo.toUpperCase()} />
          </MenuItem>
        );
      })}
      <MenuItem value="all">
        <ListItemText primary="Select All" sx={{ textAlign: 'center' }} />
      </MenuItem>
    </TextField>
  );
}

function BudgetInput() {
  const [searchParams, setSearchParams] = useSearchParams();
  const budget = searchParams.get('budget');
  const [state, setState] = React.useState(budget ?? '');

  const debouncedState = useDebouncedValue(state, 500);

  const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState(e.target.value);
  };

  React.useEffect(() => {
    if (budget !== debouncedState) {
      setSearchParams((prev) => {
        prev.set('budget', debouncedState);
        return prev;
      });
    }
  }, [budget, debouncedState, setSearchParams]);

  return (
    <TextField
      label="Budget"
      id="budget"
      name="budget"
      value={state ?? ''}
      onChange={handleBudgetChange}
      error={!!state && Number(state) < 100}
      helperText={!!state && Number(state) < 100 ? 'Minimum 100' : ''}
    />
  );
}
